/**
 * ソースプロバイダの共通インターフェース（FR-SRC）。
 *
 * engine はここで定義した型だけを見る。pdf.js の型は source/pdf/ の外へ漏らさない。
 * 要求は「目標となる物理ピクセル幅」で表現する — pdf は倍率へ、画像タイル（M4）は
 * 解像度レベルの選択へ、同じ数値から変換できる。
 */
import type { ManifestSession } from './manifest';

/** engine が描画に使える形（css2d は drawImage、webgl3d はテクスチャ転送に使う）。 */
export type PageBitmap = ImageBitmap | HTMLCanvasElement;

/**
 * ページ内の矩形（左上原点・0〜1 の正規化座標）。
 *
 * ズーム時に「可視範囲だけを高解像度で」要求するための単位（設計書 L361 の
 * 部分再描画＋上限キャップ）。ページ全体は FULL_PAGE。
 */
export interface PageRegion {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const FULL_PAGE: PageRegion = { x: 0, y: 0, w: 1, h: 1 };

export function sameRegion(a: PageRegion | null, b: PageRegion | null): boolean {
  if (a === null || b === null) return a === b;
  const eq = (p: number, q: number): boolean => Math.abs(p - q) < 1e-6;
  return eq(a.x, b.x) && eq(a.y, b.y) && eq(a.w, b.w) && eq(a.h, b.h);
}

/** 領域がページ全体を覆っているか（＝下地で足りる）。 */
export function isFullPage(region: PageRegion): boolean {
  return sameRegion(region, FULL_PAGE);
}

export interface PageRasterRequest {
  pageNo: number;
  /**
   * 目標の物理ピクセル幅（devicePixelRatio 込み）。供給上限で切り詰められることがある。
   * region 指定時は「その領域の」幅であり、ページ全面換算では targetWidth / region.w になる。
   */
  targetWidth: number;
  /** 高解像度で欲しい可視領域（省略＝ページ全体）。 */
  region?: PageRegion;
  signal?: AbortSignal;
}

/** ラスタ結果。所有権は要求側にあり、不要になったら close() で解放する（NFR-PERF-01）。 */
export interface PageRaster {
  readonly pageNo: number;
  readonly bitmap: PageBitmap;
  readonly width: number;
  readonly height: number;
  /** このラスタが表す領域。配置はこれを正とする（要求どおりとは限らない）。 */
  readonly region: PageRegion;
  close(): void;
}

export interface SourceProvider {
  /** マニフェストを受け取って開く。 */
  open(session: ManifestSession): Promise<void>;
  readonly pageCount: number;
  /** ページ（または領域）をラスタライズする。中断は signal で行う。 */
  renderPage(request: PageRasterRequest): Promise<PageRaster>;
  /** ラスタ 1 枚として供給できる最大ピクセル幅（canvas / テクスチャの上限）。 */
  maxRasterWidth(pageNo: number): number;
  /**
   * ページ全面換算での最大有効解像度（拡大上限 = 供給できる情報量）。
   *
   * maxRasterWidth が「1 枚のラスタの上限」なのに対し、こちらは「原本が持つ情報の上限」。
   * 領域を切って要求すれば、1 枚の上限を超えない範囲で実効解像度をここまで上げられる。
   */
  maxEffectiveWidth(pageNo: number): number;
  /**
   * 原本が許している操作（FR-DL-02）。PDF 配信モードだけが答えられる。
   * 画像配信モードには原本の概念が無いため実装しない（＝ダウンロード・印刷も出さない）。
   */
  permissions?(): Promise<{ download: boolean; print: boolean }>;
  /** 資源（worker 等）を解放する。 */
  dispose(): Promise<void>;
}
